import React, { useCallback, useId, useRef, useState } from "react";

import { Localized, useLocalization } from "@fluent/react";
import cc from "classcat";

import styles from "./Dropzone.module.scss";

interface DropzoneProps {
  onFileSelected: (file: File) => void;
  acceptMimeTypes?: readonly string[];
  disabled?: boolean;
  /**
   * "stacked" is the tall dashed drop target used in the upload dialog.
   * "inline" is the single-row solid card the details panel uses, where a
   * 180px-tall target would dominate the section.
   */
  layout?: "stacked" | "inline";
}

const Dropzone = ({
  onFileSelected,
  acceptMimeTypes,
  disabled = false,
  layout = "stacked",
}: DropzoneProps) => {
  const { l10n } = useLocalization();
  const inputRef = useRef<HTMLInputElement>(null);
  const inputId = useId();
  const [isDragActive, setIsDragActive] = useState(false);
  // Depth counter avoids drag-active flicker as the cursor crosses child
  // boundaries: dragleave fires on the parent before dragenter fires on the
  // child, so a naive boolean toggles off-then-on between events.
  const dragDepth = useRef(0);

  const handleSelect = useCallback(
    (files: FileList | null) => {
      if (!files || files.length === 0) return;
      onFileSelected(files[0]);
    },
    [onFileSelected]
  );

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      handleSelect(event.target.files);
      // Reset the value so re-picking the same file fires onChange again.
      if (inputRef.current) inputRef.current.value = "";
    },
    [handleSelect]
  );

  const handleDragEnter = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    dragDepth.current += 1;
    if (dragDepth.current === 1) setIsDragActive(true);
  }, []);

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const handleDragLeave = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setIsDragActive(false);
  }, []);

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      event.stopPropagation();
      dragDepth.current = 0;
      setIsDragActive(false);
      if (disabled) return;
      handleSelect(event.dataTransfer.files);
    },
    [disabled, handleSelect]
  );

  const acceptValue =
    acceptMimeTypes && acceptMimeTypes.length > 0
      ? acceptMimeTypes.join(",")
      : ".pdf,.jpg,.jpeg,.png";

  return (
    <label
      htmlFor={inputId}
      data-testid="receipt-dropzone"
      aria-disabled={disabled || undefined}
      className={cc([
        styles.dropzone,
        styles[layout],
        { [styles["drag-active"]]: isDragActive && !disabled },
      ])}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept={acceptValue}
        className={styles.input}
        disabled={disabled}
        onChange={handleChange}
      />
      <div className={styles["icon-chip"]} aria-hidden="true">
        <span className="narmi-icon-upload" />
      </div>
      <div className={styles.text}>
        <Localized
          id="receipts-upload-dropzone-instruction"
          elems={{ click: <strong /> }}
        >
          <p className={styles.instruction}>
            {"Drag and drop here or "}
            <strong>click</strong>
            {" to upload a file"}
          </p>
        </Localized>
        <p className={styles.subtext}>
          {l10n.getString("receipts-upload-dropzone-helper")}
        </p>
      </div>
    </label>
  );
};

export default Dropzone;
